/**
 * Mock Data for Studio Section — Tools Tab
 *
 * Two-layer architecture (ADR-010):
 * Layer 1 (CATALOG): workspace_tools table — single source of truth for WHAT tools exist
 * Layer 2 (EXECUTORS): code registries handle HOW to run tools (not user-facing)
 *
 * Tool sources: builtin, mcp, openapi, custom, database
 * Tools from MCP servers and API connections are derived from mock-connections-data.
 */

import {
  MOCK_MCP_SERVERS,
  MOCK_API_CONNECTIONS,
  type McpTool,
} from './mock-connections-data';

// ══════════════════════════════════════════════════════════════════════════
// SKILLS
// ══════════════════════════════════════════════════════════════════════════

export type SkillCategory = 'maintenance' | 'safety' | 'compliance' | 'operations' | 'documentation' | 'analytics' | 'custom';
export type SkillStatus = 'active' | 'draft' | 'archived';

export interface SkillKnowledgeDoc {
  id: string;
  name: string;
  type: 'pdf' | 'docx' | 'md' | 'txt' | 'xlsx';
  size: number;
  addedAt: string;
}

export interface StudioSkill {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  iconColor: string;
  category: SkillCategory;
  status: SkillStatus;
  isBuiltin: boolean;
  version: string;
  instructions: string;
  requiredTools: string[];
  knowledgeDocs: SkillKnowledgeDoc[];
  agentGrants: string[];
  tags: string[];
  createdBy: string;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const SKILL_CATEGORY_LABELS: Record<SkillCategory, { label: string; color: string }> = {
  maintenance: { label: 'Maintenance', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' },
  safety: { label: 'Safety', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  compliance: { label: 'Compliance', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  operations: { label: 'Operations', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  documentation: { label: 'Documentation', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  analytics: { label: 'Analytics', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  custom: { label: 'Custom', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

export const SKILL_STATUS_STYLES: Record<SkillStatus, string> = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  draft: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  archived: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-500',
};

export const MOCK_STUDIO_SKILLS: StudioSkill[] = [
  {
    id: 'skill-maintenance',
    name: 'Maintenance Analysis',
    icon: 'activity', iconColor: 'cyan',
    slug: 'maintenance_analysis',
    description: 'Analyze equipment health data, detect anomalies, predict failures, and recommend preventive maintenance actions.',
    category: 'maintenance',
    status: 'active',
    isBuiltin: true,
    version: '2.1.0',
    instructions: `# Maintenance Analysis

## Purpose
Guide the agent through equipment health analysis, from data collection to actionable recommendations.

## Steps

1. **Identify the equipment** — Resolve the equipment tag (e.g., P-401) to a full asset record using \`iq.asset.get\`. Confirm with the user if ambiguous.

2. **Collect health data** — Retrieve:
   - Current sensor readings via \`iq.asset.getTelemetry\`
   - Maintenance history via \`iq.asset.getHistory\`
   - Work order history from CMMS

3. **Analyze trends** — Look for:
   - MTBF/MTTR trends over the last 12 months
   - Vibration patterns (increasing RMS = bearing wear)
   - Temperature anomalies (>10% above baseline)
   - Oil analysis results if available

4. **Compare against baselines** — Check manufacturer specs and historical norms. Flag any parameter exceeding:
   - Warning: >80% of alarm threshold
   - Critical: >90% of alarm threshold

5. **Generate recommendations** — Based on findings:
   - If degrading: Recommend PM scheduling with specific tasks
   - If critical: Recommend immediate inspection with LOTO reference
   - If stable: Confirm health status with next PM date

## Rules
- **Always cite specific data points with timestamps**
- Never recommend skipping a scheduled PM without supervisor approval
- Include confidence level for predictions (low/medium/high)
- For rotating equipment, always check vibration + temperature + oil together`,
    requiredTools: ['iq.asset.get', 'iq.asset.getHistory', 'iq.asset.getTelemetry'],
    knowledgeDocs: [
      { id: 'doc-1', name: 'Vibration Analysis Standards ISO 10816.pdf', type: 'pdf', size: 2400000, addedAt: '2026-03-01T00:00:00Z' },
      { id: 'doc-2', name: 'Equipment Criticality Matrix.xlsx', type: 'xlsx', size: 180000, addedAt: '2026-03-01T00:00:00Z' },
      { id: 'doc-3', name: 'PM Task Templates.md', type: 'md', size: 45000, addedAt: '2026-03-15T00:00:00Z' },
    ],
    agentGrants: ['maintenance', 'reliability', 'troubleshoot', 'ask-argus'],
    tags: ['equipment', 'predictive', 'vibration', 'MTBF', 'PM'],
    createdBy: 'System',
    usageCount: 1240,
    lastUsedAt: '2026-04-18T09:15:00Z',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-10T00:00:00Z',
  },
  {
    id: 'skill-safety',
    name: 'Safety Procedure Lookup',
    icon: 'shield-alert', iconColor: 'red',
    slug: 'safety_lookup',
    description: 'Find and present safety procedures including LOTO sequences, safety permits, and hazard assessments. SAFETY-CRITICAL: never abbreviate procedures.',
    category: 'safety',
    status: 'active',
    isBuiltin: true,
    version: '1.3.0',
    instructions: `# Safety Procedure Lookup

## Purpose
Retrieve and present safety-critical procedures with absolute accuracy. No abbreviation, no summarization of safety steps.

## CRITICAL RULES
- **NEVER abbreviate LOTO sequences** — present ALL steps in order
- **NEVER omit ANSI Z535.6 signal words** (DANGER, WARNING, CAUTION) — include exactly as written
- **NEVER summarize PPE requirements** — list every item
- If a procedure references another procedure, include the reference with document ID

## Steps

1. **Identify the procedure type** — Determine if the user needs:
   - LOTO (Lock-Out/Tag-Out)
   - Hot work permit
   - Confined space entry
   - Chemical handling
   - Height work
   - General safety procedure

2. **Search documents** — Use \`iq.document.search\` with the equipment tag AND procedure type. Always search by both.

3. **Retrieve full content** — Use \`iq.document.getContent\` to get the complete procedure. **Do not truncate.**

4. **Present with signal words** — Format the output with:
   - ⛔ DANGER: Immediate hazard, death or serious injury
   - ⚠️ WARNING: Potential hazard, death or serious injury
   - ⚡ CAUTION: Potential hazard, minor or moderate injury

5. **Verify currency** — Check document revision date. If older than 12 months, warn the user to verify with safety department.

## Knowledge References
- All facility SOPs in the Safety folder
- OSHA 1910.147 (LOTO standard)
- ANSI Z535.6 (Signal word standard)`,
    requiredTools: ['iq.asset.get', 'iq.asset.getDocuments', 'iq.document.search', 'iq.document.getContent'],
    knowledgeDocs: [
      { id: 'doc-4', name: 'OSHA 1910.147 LOTO Standard.pdf', type: 'pdf', size: 890000, addedAt: '2026-01-20T00:00:00Z' },
      { id: 'doc-5', name: 'ANSI Z535.6 Signal Words Guide.pdf', type: 'pdf', size: 1200000, addedAt: '2026-01-20T00:00:00Z' },
      { id: 'doc-6', name: 'Facility Safety SOP Index.docx', type: 'docx', size: 340000, addedAt: '2026-02-10T00:00:00Z' },
    ],
    agentGrants: ['safety', 'docs', 'ask-argus'],
    tags: ['LOTO', 'safety', 'procedures', 'PPE', 'OSHA', 'ANSI'],
    createdBy: 'System',
    usageCount: 680,
    lastUsedAt: '2026-04-18T08:30:00Z',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-20T00:00:00Z',
  },
  {
    id: 'skill-docqa',
    name: 'Document Q&A',
    icon: 'file-search', iconColor: 'blue',
    slug: 'document_qa',
    description: 'Answer questions from SOPs, manuals, and P&IDs with precise citations including document name, revision, page, and section.',
    category: 'documentation',
    status: 'active',
    isBuiltin: true,
    version: '1.5.0',
    instructions: `# Document Q&A

## Purpose
Answer user questions by finding and citing specific passages from the document library.

## Steps

1. **Understand the question** — Classify the query:
   - Factual lookup (specific value, procedure step, spec)
   - Comparison (two documents, two revisions)
   - Interpretation (meaning of a diagram, symbol, code)

2. **Search the knowledge base** — Use \`iq.document.search\` with relevant keywords. Try both exact terms and synonyms.

3. **Retrieve content** — Use \`iq.document.getContent\` for top-ranked results. Check if the answer is in the retrieved chunks.

4. **Cite precisely** — Every answer must include:
   - Document name
   - Revision/version number
   - Page number (if available)
   - Section heading

5. **Handle version conflicts** — If multiple revisions exist, use the LATEST unless the user specifies otherwise. Always note which revision you're citing.

## Rules
- Always use the latest document revision unless asked otherwise
- If uncertain, say "Based on [Document v2.1], the answer appears to be X, but I recommend verifying with the engineering team"
- Never fabricate citations — if you can't find the answer, say so`,
    requiredTools: ['iq.document.search', 'iq.document.getContent', 'iq.document.getVersions'],
    knowledgeDocs: [],
    agentGrants: ['docs', 'ask-argus', 'troubleshoot', 'compliance'],
    tags: ['citations', 'SOPs', 'manuals', 'P&ID', 'documents'],
    createdBy: 'System',
    usageCount: 2100,
    lastUsedAt: '2026-04-18T09:20:00Z',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-05T00:00:00Z',
  },
  {
    id: 'skill-parts',
    name: 'Parts Search',
    icon: 'database', iconColor: 'teal',
    slug: 'parts_search',
    description: 'Find parts by number or description, check inventory levels, suggest alternatives, and cross-reference OEM catalogs.',
    category: 'operations',
    status: 'active',
    isBuiltin: true,
    version: '1.1.0',
    instructions: `# Parts Search

## Steps
1. Search by OEM part number, description, or equipment tag
2. Check current inventory levels at all warehouses
3. If out of stock, suggest alternative/compatible parts
4. Include cross-reference numbers (OEM, vendor, internal)
5. Show lead time and cost if available

## Rules
- Always include OEM part number AND internal stock number
- Flag safety-critical parts (must be OEM, no substitutes)
- Show min-stock level vs current level`,
    requiredTools: ['iq.asset.get', 'iq.asset.getDocuments'],
    knowledgeDocs: [
      { id: 'doc-7', name: 'Parts Cross-Reference Catalog.xlsx', type: 'xlsx', size: 5600000, addedAt: '2026-02-01T00:00:00Z' },
    ],
    agentGrants: ['parts', 'work-order', 'maintenance'],
    tags: ['parts', 'inventory', 'OEM', 'procurement'],
    createdBy: 'System',
    usageCount: 890,
    lastUsedAt: '2026-04-18T07:45:00Z',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-03-10T00:00:00Z',
  },
  {
    id: 'skill-compliance',
    name: 'Compliance Check',
    icon: 'scale', iconColor: 'violet',
    slug: 'compliance_check',
    description: 'Verify compliance status against ITAR, CMMC, HIPAA, and other regulatory frameworks. Track certifications and flag expirations.',
    category: 'compliance',
    status: 'active',
    isBuiltin: true,
    version: '1.2.0',
    instructions: `# Compliance Check

## Steps
1. Identify the applicable compliance framework(s)
2. Search for current certification status and expiry dates
3. Cross-reference against framework requirements checklist
4. Flag any expired certifications or upcoming renewals (within 90 days)
5. Generate a compliance status summary with RAG indicators (Red/Amber/Green)

## Rules
- Verify against applicable framework requirements
- Flag expired certifications with urgency level
- For ITAR: verify US Person status before disclosing controlled information
- Always include the compliance officer contact for escalation`,
    requiredTools: ['iq.document.search', 'iq.document.list'],
    knowledgeDocs: [
      { id: 'doc-8', name: 'ITAR Compliance Checklist.pdf', type: 'pdf', size: 780000, addedAt: '2026-02-15T00:00:00Z' },
      { id: 'doc-9', name: 'CMMC Level 2 Controls Matrix.xlsx', type: 'xlsx', size: 420000, addedAt: '2026-02-15T00:00:00Z' },
    ],
    agentGrants: ['compliance', 'docs'],
    tags: ['ITAR', 'CMMC', 'HIPAA', 'certifications', 'regulatory'],
    createdBy: 'System',
    usageCount: 340,
    lastUsedAt: '2026-04-17T14:00:00Z',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-04-01T00:00:00Z',
  },
  {
    id: 'skill-shift-report',
    name: 'Shift Handover Report',
    icon: 'notebook', iconColor: 'amber',
    slug: 'shift_report',
    description: 'Generate structured shift handover reports collecting alarms, work completed, open issues, and safety observations.',
    category: 'operations',
    status: 'active',
    isBuiltin: false,
    version: '1.0.0',
    instructions: `# Shift Handover Report Generation

## Purpose
Collect and organize all relevant shift data into a structured handover report.

## Report Sections
1. **Shift Summary** — Date, shift, area, personnel on shift
2. **Alarms & Events** — All alarms during shift with resolution status
3. **Work Completed** — Work orders closed during shift
4. **Work In Progress** — Open work orders with status update
5. **Safety Observations** — Any safety incidents, near-misses, or observations
6. **Equipment Status Changes** — Any equipment taken offline or brought online
7. **Handover Notes** — Free-form notes from outgoing shift to incoming

## Rules
- Include ALL alarms, even acknowledged ones
- Flag any safety observations with priority
- Note any pending approvals or decisions needed`,
    requiredTools: ['iq.asset.get', 'iq.telemetry.getAlarms', 'iq.cmms.searchWorkOrders'],
    knowledgeDocs: [
      { id: 'doc-10', name: 'Shift Report Template.md', type: 'md', size: 12000, addedAt: '2026-03-20T00:00:00Z' },
    ],
    agentGrants: ['shift-handover'],
    tags: ['shift', 'handover', 'report', 'alarms'],
    createdBy: 'Admin',
    usageCount: 420,
    lastUsedAt: '2026-04-18T06:00:00Z',
    createdAt: '2026-03-20T00:00:00Z',
    updatedAt: '2026-04-15T00:00:00Z',
  },
  {
    id: 'skill-work-planning',
    name: 'Work Order Planning',
    icon: 'clipboard-check', iconColor: 'orange',
    slug: 'work_planning',
    description: 'Plan work orders with resource allocation, scheduling, parts requirements, and safety permits.',
    category: 'operations',
    status: 'active',
    isBuiltin: false,
    version: '1.0.0',
    instructions: `# Work Order Planning

## Steps
1. Identify the scope of work (corrective, preventive, project)
2. Determine required resources (labor, parts, equipment, permits)
3. Check parts availability and order if needed
4. Schedule based on equipment availability and crew capacity
5. Identify required safety permits (LOTO, hot work, confined space)
6. Generate work order with all details

## Rules
- Always check parts availability before scheduling
- Include safety permit requirements
- Estimate duration based on historical similar work orders
- Flag if work requires outage or reduced production`,
    requiredTools: ['iq.asset.get', 'iq.cmms.searchWorkOrders', 'iq.cmms.createWorkOrder'],
    knowledgeDocs: [],
    agentGrants: ['work-order', 'maintenance'],
    tags: ['work-orders', 'planning', 'scheduling', 'resources'],
    createdBy: 'Admin',
    usageCount: 567,
    lastUsedAt: '2026-04-18T08:00:00Z',
    createdAt: '2026-03-25T00:00:00Z',
    updatedAt: '2026-04-12T00:00:00Z',
  },
  {
    id: 'skill-vibration-analysis',
    name: 'Vibration Analysis Expert',
    icon: 'gauge', iconColor: 'emerald',
    slug: 'vibration_expert',
    description: 'Advanced vibration analysis with ISO 10816 severity classification, spectral pattern recognition, and bearing defect frequency calculation.',
    category: 'analytics',
    status: 'draft',
    isBuiltin: false,
    version: '0.2.0',
    instructions: `# Vibration Analysis Expert

## Purpose
Perform advanced vibration analysis beyond basic threshold checks.

## Steps
1. Collect vibration data (acceleration, velocity, displacement)
2. Classify severity per ISO 10816 (Good/Satisfactory/Unsatisfactory/Unacceptable)
3. Analyze spectral patterns for common fault signatures:
   - 1x RPM: Imbalance
   - 2x RPM: Misalignment
   - BPFO/BPFI: Bearing defects
   - Gear mesh frequencies: Gear damage
4. Calculate bearing defect frequencies from manufacturer data
5. Compare trends and recommend action

## Status: DRAFT — needs validation by reliability engineering team`,
    requiredTools: ['iq.asset.get', 'iq.asset.getTelemetry'],
    knowledgeDocs: [
      { id: 'doc-11', name: 'Vibration Analysis Standards ISO 10816.pdf', type: 'pdf', size: 2400000, addedAt: '2026-04-10T00:00:00Z' },
    ],
    agentGrants: ['reliability'],
    tags: ['vibration', 'ISO 10816', 'spectral', 'bearings', 'advanced'],
    createdBy: 'Data Science Team',
    usageCount: 45,
    lastUsedAt: '2026-04-16T10:00:00Z',
    createdAt: '2026-04-10T00:00:00Z',
    updatedAt: '2026-04-16T10:00:00Z',
  },
];

// Note: doc-1 in skill-maintenance and doc-11 in skill-vibration both reference
// the same physical document (ISO 10816) but are separate attachment records.

// ── Types ──────────────────────────────────────────────────────────────────

export type ToolSource = 'builtin' | 'mcp' | 'openapi' | 'custom' | 'database';
export type ToolPolicy = 'auto_approve' | 'require_approval' | 'forbidden';
export type ToolNamespace = 'builtin' | 'iq' | 'ext' | 'forge' | 'api' | 'custom' | 'db';

export interface ToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  required: boolean;
  description: string;
  defaultValue?: string;
  enum?: string[];
}

export interface AdminValve {
  key: string;
  value: string | number | boolean;
  type: 'string' | 'number' | 'boolean';
  description: string;
}

export interface UserValveSchema {
  key: string;
  type: 'string' | 'number' | 'boolean';
  description: string;
  defaultValue?: unknown;
}

export interface StudioTool {
  id: string;
  name: string;
  displayName: string;
  description: string;
  toolSource: ToolSource;
  namespace: ToolNamespace;
  parameters: ToolParameter[];
  policy: ToolPolicy;
  isActive: boolean;
  isBuiltin: boolean;
  isWriteOperation: boolean;
  version: number;
  connectionId?: string;
  connectionName?: string;
  adminValves: AdminValve[];
  userValvesSchema: UserValveSchema[];
  agentGrants: string[];
  metrics: {
    callCount: number;
    avgLatencyMs: number;
    errorRate: number;
    lastCalledAt: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ToolSection {
  key: string;
  label: string;
  description: string;
  infoTag?: string;
  tools: StudioTool[];
}

export interface RecentToolCall {
  id: string;
  toolId: string;
  toolName: string;
  agentName: string;
  timestamp: string;
  durationMs: number;
  success: boolean;
  error?: string;
}

// ── Label Maps ─────────────────────────────────────────────────────────────

// Canonical source labels — Codex audit F-S25 / #25. Studio's
// /studio/tools view, Studio's tool detail view, and any consumer of
// `SourceBadge` (Agents builder etc.) MUST agree on these strings.
// Custom Functions surface as a sub-marker on `custom` rows where
// handler_config.kind === 'custom_function'; the Studio Tool list
// renders the sub-marker label "Custom Function" inline. Until the
// schema gains a first-class `'custom_function'` source, this map is
// the single canonical source of truth for labels.
export const SOURCE_LABELS: Record<ToolSource, string> = {
  builtin: 'Built-in',
  mcp: 'MCP',
  openapi: 'OpenAPI',
  custom: 'HTTP',
  database: 'Database',
};

export const SOURCE_COLORS: Record<ToolSource, string> = {
  builtin: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
  mcp: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  openapi: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  custom: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  database: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
};

export const POLICY_LABELS: Record<ToolPolicy, string> = {
  auto_approve: 'Auto',
  require_approval: 'Approval',
  forbidden: 'Forbidden',
};

export const POLICY_COLORS: Record<ToolPolicy, string> = {
  auto_approve: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  require_approval: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  forbidden: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

export const POLICY_DESCRIPTIONS: Record<ToolPolicy, string> = {
  auto_approve: 'Execute immediately — suitable for read-only operations',
  require_approval: 'Pause for human confirmation before executing',
  forbidden: 'Reject outright — block all invocations of this tool',
};

// ── Section Config ─────────────────────────────────────────────────────────

const SECTION_CONFIG: Record<string, { label: string; description: string; infoTag?: string }> = {
  builtin: { label: 'Built-in Tools', description: 'Core platform tools available to all agents', infoTag: 'Auto-seeded' },
  'mcp-iq': { label: 'IQ Native (MCP Tier 1)', description: 'In-process tools with direct RLS-enforced database access' },
  'mcp-ext': { label: 'External (MCP Tier 2)', description: 'Docker-sandboxed connectors to enterprise systems' },
  'mcp-forge': { label: 'Forge (MCP Tier 3)', description: 'Auto-generated MCP servers from API specifications' },
  openapi: { label: 'API Connections', description: 'Tools auto-generated from OpenAPI/GraphQL specifications' },
  custom: { label: 'Custom Tools', description: 'Admin-created HTTP request and database query tools' },
};

// ── Builtin Tools ──────────────────────────────────────────────────────────

const BUILTIN_TOOLS: StudioTool[] = [
  {
    id: 'tool-builtin-time',
    name: 'builtin.get_current_time',
    displayName: 'Get Current Time',
    description: 'Returns the current date and time in the specified timezone',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'timezone', type: 'string', required: false, description: 'IANA timezone (e.g., America/Chicago)', defaultValue: 'UTC' },
      { name: 'format', type: 'string', required: false, description: 'Output format: iso, unix, human', defaultValue: 'iso', enum: ['iso', 'unix', 'human'] },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: false,
    version: 1,
    adminValves: [],
    userValvesSchema: [{ key: 'defaultTimezone', type: 'string', description: 'Default timezone for this user', defaultValue: 'UTC' }],
    agentGrants: ['ask-argus', 'maintenance', 'shift-handover', 'troubleshoot'],
    metrics: { callCount: 2340, avgLatencyMs: 5, errorRate: 0, lastCalledAt: '2026-04-18T09:15:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'tool-builtin-calc',
    name: 'builtin.calculate',
    displayName: 'Calculate',
    description: 'Mathematical calculations, unit conversions, and formula evaluation',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'expression', type: 'string', required: true, description: 'Math expression to evaluate (e.g., "100 * 1.8 + 32")' },
      { name: 'precision', type: 'number', required: false, description: 'Decimal precision', defaultValue: '4' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: false,
    version: 1,
    adminValves: [{ key: 'maxExpressionLength', value: 500, type: 'number', description: 'Maximum expression length' }],
    userValvesSchema: [],
    agentGrants: ['ask-argus', 'reliability', 'maintenance'],
    metrics: { callCount: 1560, avgLatencyMs: 8, errorRate: 0.001, lastCalledAt: '2026-04-18T08:42:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'tool-builtin-knowledge',
    name: 'builtin.search_knowledge',
    displayName: 'Search Knowledge',
    description: 'Hybrid search (dense + keyword + RRF) across the document knowledge base with re-ranking',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'query', type: 'string', required: true, description: 'Natural language search query' },
      { name: 'maxResults', type: 'number', required: false, description: 'Maximum results to return', defaultValue: '5' },
      { name: 'collection', type: 'string', required: false, description: 'Specific collection to search (default: all)' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: false,
    version: 2,
    adminValves: [
      { key: 'maxResults', value: 10, type: 'number', description: 'Maximum results limit' },
      { key: 'minScore', value: 0.7, type: 'number', description: 'Minimum relevance score threshold' },
      { key: 'rerankerEnabled', value: true, type: 'boolean', description: 'Enable BGE re-ranker' },
    ],
    userValvesSchema: [{ key: 'preferredCollections', type: 'string', description: 'Comma-separated preferred collection IDs' }],
    agentGrants: ['ask-argus', 'docs', 'safety', 'compliance', 'troubleshoot', 'maintenance'],
    metrics: { callCount: 4521, avgLatencyMs: 245, errorRate: 0.002, lastCalledAt: '2026-04-18T09:20:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-15T10:00:00Z',
  },
  {
    id: 'tool-builtin-memory-add',
    name: 'builtin.add_memory',
    displayName: 'Add Memory',
    description: 'Store important facts, user preferences, or context for later recall',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'content', type: 'string', required: true, description: 'Content to remember' },
      { name: 'scope', type: 'string', required: false, description: 'Memory scope', defaultValue: 'user', enum: ['user', 'organization', 'session'] },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: true,
    version: 1,
    adminValves: [{ key: 'maxMemorySize', value: 1000, type: 'number', description: 'Max characters per memory entry' }],
    userValvesSchema: [],
    agentGrants: ['ask-argus'],
    metrics: { callCount: 890, avgLatencyMs: 120, errorRate: 0, lastCalledAt: '2026-04-18T07:55:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'tool-builtin-memory-search',
    name: 'builtin.search_memories',
    displayName: 'Search Memories',
    description: 'Search stored memories by semantic similarity',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'query', type: 'string', required: true, description: 'Search query' },
      { name: 'scope', type: 'string', required: false, description: 'Memory scope to search', enum: ['user', 'organization', 'all'] },
      { name: 'limit', type: 'number', required: false, description: 'Max results', defaultValue: '5' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: false,
    version: 1,
    adminValves: [],
    userValvesSchema: [],
    agentGrants: ['ask-argus'],
    metrics: { callCount: 1234, avgLatencyMs: 85, errorRate: 0.001, lastCalledAt: '2026-04-18T09:10:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'tool-builtin-web',
    name: 'builtin.web_search',
    displayName: 'Web Search',
    description: 'Search the web via SearXNG (requires feature toggle)',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'query', type: 'string', required: true, description: 'Search query' },
      { name: 'maxResults', type: 'number', required: false, description: 'Maximum results', defaultValue: '5' },
    ],
    policy: 'auto_approve',
    isActive: false,
    isBuiltin: true,
    isWriteOperation: false,
    version: 1,
    adminValves: [
      { key: 'searxngUrl', value: 'http://searxng:8080', type: 'string', description: 'SearXNG instance URL' },
      { key: 'maxResults', value: 10, type: 'number', description: 'Global max results limit' },
    ],
    userValvesSchema: [],
    agentGrants: [],
    metrics: { callCount: 0, avgLatencyMs: 0, errorRate: 0, lastCalledAt: null },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'tool-builtin-skill',
    name: 'builtin.view_skill',
    displayName: 'View Skill',
    description: 'Load full instructions for a specific skill (lazy-loaded)',
    toolSource: 'builtin',
    namespace: 'builtin',
    parameters: [
      { name: 'skillId', type: 'string', required: true, description: 'Skill identifier (e.g., maintenance_analysis)' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: true,
    isWriteOperation: false,
    version: 1,
    adminValves: [],
    userValvesSchema: [],
    agentGrants: ['ask-argus', 'maintenance', 'safety', 'docs', 'compliance', 'troubleshoot'],
    metrics: { callCount: 680, avgLatencyMs: 12, errorRate: 0, lastCalledAt: '2026-04-18T08:30:00Z' },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ── MCP Tools (derived from connections data) ──────────────────────────────

function buildMcpTools(): StudioTool[] {
  const tools: StudioTool[] = [];
  for (const server of MOCK_MCP_SERVERS) {
    const ns: ToolNamespace = server.tier === 'tier1' ? 'iq' : server.tier === 'tier3' ? 'forge' : 'ext';
    for (const tool of server.tools) {
      tools.push({
        id: `tool-mcp-${tool.name.replace(/\./g, '-')}`,
        name: tool.name,
        displayName: tool.name.split('.').pop()?.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim() ?? tool.name,
        description: tool.description,
        toolSource: 'mcp',
        namespace: ns,
        parameters: [
          { name: 'query', type: 'string', required: true, description: 'Search or lookup parameter' },
        ],
        policy: tool.permissionLevel === 'admin_only' ? 'forbidden' as ToolPolicy : tool.permissionLevel as ToolPolicy,
        isActive: server.status !== 'offline',
        isBuiltin: false,
        isWriteOperation: tool.isWriteOperation ?? false,
        version: 1,
        connectionId: server.id,
        connectionName: server.displayName,
        adminValves: [
          { key: 'timeout', value: server.tier === 'tier1' ? 5000 : 30000, type: 'number', description: 'Request timeout (ms)' },
        ],
        userValvesSchema: [],
        agentGrants: tool.isWriteOperation ? ['work-order'] : ['ask-argus', 'maintenance', 'troubleshoot'],
        metrics: {
          callCount: tool.callCount,
          avgLatencyMs: tool.avgLatencyMs,
          errorRate: server.metrics.errorRate,
          lastCalledAt: (tool.callCount ?? 0) > 0 ? '2026-04-18T09:00:00Z' : null,
        },
        createdAt: server.createdAt,
        updatedAt: server.updatedAt,
      });
    }
  }
  return tools;
}

// ── OpenAPI Tools (derived from API connections) ───────────────────────────

function buildOpenApiTools(): StudioTool[] {
  const tools: StudioTool[] = [];
  for (const conn of MOCK_API_CONNECTIONS) {
    for (const ep of conn.endpoints.filter(e => e.enabled)) {
      tools.push({
        id: `tool-api-${ep.id}`,
        name: `api.${conn.id.replace('api-', '')}.${ep.operationId}`,
        displayName: ep.operationId.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
        description: ep.summary,
        toolSource: 'openapi',
        namespace: 'api',
        parameters: [
          { name: 'params', type: 'object', required: false, description: 'Request parameters' },
        ],
        policy: ep.category === 'admin' ? 'forbidden' : ep.category === 'write' ? 'require_approval' : 'auto_approve',
        isActive: conn.status !== 'offline',
        isBuiltin: false,
        isWriteOperation: ep.category !== 'read',
        version: 1,
        connectionId: conn.id,
        connectionName: conn.displayName,
        adminValves: [
          { key: 'timeout', value: 30000, type: 'number', description: 'Request timeout (ms)' },
        ],
        userValvesSchema: [],
        agentGrants: ep.category === 'read' ? ['ask-argus', 'maintenance'] : ['work-order'],
        metrics: {
          callCount: ep.callCount,
          avgLatencyMs: ep.avgLatencyMs,
          errorRate: conn.metrics.errorRate,
          lastCalledAt: ep.callCount > 0 ? '2026-04-18T08:45:00Z' : null,
        },
        createdAt: conn.createdAt,
        updatedAt: conn.updatedAt,
      });
    }
  }
  return tools;
}

// ── Custom Tools ───────────────────────────────────────────────────────────

const CUSTOM_TOOLS: StudioTool[] = [
  {
    id: 'tool-custom-valve-status',
    name: 'custom.check_valve_status',
    displayName: 'Check Valve Status',
    description: 'GET request to plant PLC API to check control valve position and status',
    toolSource: 'custom',
    namespace: 'custom',
    parameters: [
      { name: 'valveId', type: 'string', required: true, description: 'Valve tag ID (e.g., FV-401)' },
      { name: 'includeHistory', type: 'boolean', required: false, description: 'Include recent position history', defaultValue: 'false' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: false,
    isWriteOperation: false,
    version: 1,
    adminValves: [
      { key: 'plcApiUrl', value: 'https://plc-gw.plant.local:9443/api', type: 'string', description: 'PLC gateway API URL' },
      { key: 'timeout', value: 5000, type: 'number', description: 'Request timeout (ms)' },
    ],
    userValvesSchema: [],
    agentGrants: ['maintenance', 'troubleshoot'],
    metrics: { callCount: 156, avgLatencyMs: 45, errorRate: 0.005, lastCalledAt: '2026-04-18T08:20:00Z' },
    createdAt: '2026-03-20T14:00:00Z',
    updatedAt: '2026-04-10T09:30:00Z',
  },
  {
    id: 'tool-custom-notify',
    name: 'custom.send_shift_notification',
    displayName: 'Send Shift Notification',
    description: 'POST to internal messaging webhook to notify operators of shift events',
    toolSource: 'custom',
    namespace: 'custom',
    parameters: [
      { name: 'channel', type: 'string', required: true, description: 'Notification channel', enum: ['operations', 'maintenance', 'safety', 'management'] },
      { name: 'message', type: 'string', required: true, description: 'Notification message content' },
      { name: 'priority', type: 'string', required: false, description: 'Message priority', defaultValue: 'normal', enum: ['low', 'normal', 'high', 'critical'] },
    ],
    policy: 'require_approval',
    isActive: true,
    isBuiltin: false,
    isWriteOperation: true,
    version: 1,
    adminValves: [
      { key: 'webhookUrl', value: 'https://msg.internal/hooks/argus', type: 'string', description: 'Webhook endpoint' },
      { key: 'maxMessageLength', value: 2000, type: 'number', description: 'Maximum message length' },
    ],
    userValvesSchema: [
      { key: 'defaultChannel', type: 'string', description: 'Default notification channel' },
    ],
    agentGrants: ['shift-handover'],
    metrics: { callCount: 89, avgLatencyMs: 320, errorRate: 0.01, lastCalledAt: '2026-04-18T06:00:00Z' },
    createdAt: '2026-03-25T11:00:00Z',
    updatedAt: '2026-04-12T16:00:00Z',
  },
  {
    id: 'tool-db-downtime',
    name: 'db.equipment_downtime_report',
    displayName: 'Equipment Downtime Report',
    description: 'SQL query against CMMS database to generate equipment downtime summary',
    toolSource: 'database',
    namespace: 'db',
    parameters: [
      { name: 'equipmentTag', type: 'string', required: true, description: 'Equipment tag or wildcard pattern' },
      { name: 'startDate', type: 'string', required: true, description: 'Start date (ISO format)' },
      { name: 'endDate', type: 'string', required: false, description: 'End date (default: now)' },
    ],
    policy: 'auto_approve',
    isActive: true,
    isBuiltin: false,
    isWriteOperation: false,
    version: 1,
    adminValves: [
      { key: 'connectionString', value: 'postgresql://cmms-ro@cmms-db:5432/cmms', type: 'string', description: 'Database connection string (read-only)' },
      { key: 'queryTimeout', value: 60000, type: 'number', description: 'Query timeout (ms)' },
      { key: 'maxRows', value: 1000, type: 'number', description: 'Maximum result rows' },
    ],
    userValvesSchema: [
      { key: 'defaultTimeRange', type: 'string', description: 'Default time range (e.g., 30d, 90d)', defaultValue: '30d' },
    ],
    agentGrants: ['reliability', 'maintenance'],
    metrics: { callCount: 234, avgLatencyMs: 1850, errorRate: 0.003, lastCalledAt: '2026-04-18T07:00:00Z' },
    createdAt: '2026-04-01T10:00:00Z',
    updatedAt: '2026-04-15T14:00:00Z',
  },
];

// ── Build complete catalog ─────────────────────────────────────────────────

export const MOCK_STUDIO_TOOLS: StudioTool[] = [
  ...BUILTIN_TOOLS,
  ...buildMcpTools(),
  ...buildOpenApiTools(),
  ...CUSTOM_TOOLS,
];

// ── Section grouping helper ────────────────────────────────────────────────

export function getToolSections(tools: StudioTool[]): ToolSection[] {
  const sections: ToolSection[] = [];

  // Builtin
  const builtins = tools.filter(t => t.toolSource === 'builtin');
  if (builtins.length > 0) sections.push({ key: 'builtin', ...SECTION_CONFIG.builtin, tools: builtins });

  // MCP by namespace
  const iqTools = tools.filter(t => t.toolSource === 'mcp' && t.namespace === 'iq');
  if (iqTools.length > 0) sections.push({ key: 'mcp-iq', ...SECTION_CONFIG['mcp-iq'], tools: iqTools });

  const extTools = tools.filter(t => t.toolSource === 'mcp' && t.namespace === 'ext');
  if (extTools.length > 0) sections.push({ key: 'mcp-ext', ...SECTION_CONFIG['mcp-ext'], tools: extTools });

  const forgeTools = tools.filter(t => t.toolSource === 'mcp' && t.namespace === 'forge');
  if (forgeTools.length > 0) sections.push({ key: 'mcp-forge', ...SECTION_CONFIG['mcp-forge'], tools: forgeTools });

  // OpenAPI
  const apiTools = tools.filter(t => t.toolSource === 'openapi');
  if (apiTools.length > 0) sections.push({ key: 'openapi', ...SECTION_CONFIG.openapi, tools: apiTools });

  // Custom + Database (intentionally grouped — both are admin-created tools)
  const customDbTools = tools.filter(t => t.toolSource === 'custom' || t.toolSource === 'database');
  if (customDbTools.length > 0) sections.push({ key: 'custom', ...SECTION_CONFIG.custom, tools: customDbTools });

  return sections;
}

// ── Mock Recent Calls ──────────────────────────────────────────────────────

export const MOCK_RECENT_CALLS: RecentToolCall[] = [
  { id: 'rc-1', toolId: 'tool-builtin-knowledge', toolName: 'builtin.search_knowledge', agentName: 'Ask Argus', timestamp: '2026-04-18T09:20:00Z', durationMs: 245, success: true },
  { id: 'rc-2', toolId: 'tool-mcp-iq-asset-search', toolName: 'iq.asset.search', agentName: 'Maintenance Intelligence', timestamp: '2026-04-18T09:18:00Z', durationMs: 32, success: true },
  { id: 'rc-3', toolId: 'tool-mcp-iq-telemetry-getCurrent', toolName: 'iq.telemetry.getCurrent', agentName: 'Ask Argus', timestamp: '2026-04-18T09:15:00Z', durationMs: 28, success: true },
  { id: 'rc-4', toolId: 'tool-mcp-ext-sap-searchParts', toolName: 'ext.sap.searchParts', agentName: 'Parts & Inventory', timestamp: '2026-04-18T09:12:00Z', durationMs: 850, success: true },
  { id: 'rc-5', toolId: 'tool-mcp-iq-cmms-createWorkOrder', toolName: 'iq.cmms.createWorkOrder', agentName: 'Work Order Planner', timestamp: '2026-04-18T09:08:00Z', durationMs: 120, success: true },
  { id: 'rc-6', toolId: 'tool-mcp-ext-snow-searchIncidents', toolName: 'ext.snow.searchIncidents', agentName: 'Incident Analysis', timestamp: '2026-04-18T09:05:00Z', durationMs: 0, success: false, error: 'Circuit breaker open: ServiceNow ITSM offline' },
  { id: 'rc-7', toolId: 'tool-custom-valve-status', toolName: 'custom.check_valve_status', agentName: 'Troubleshooting', timestamp: '2026-04-18T08:58:00Z', durationMs: 45, success: true },
  { id: 'rc-8', toolId: 'tool-builtin-calc', toolName: 'builtin.calculate', agentName: 'Ask Argus', timestamp: '2026-04-18T08:52:00Z', durationMs: 8, success: true },
  { id: 'rc-9', toolId: 'tool-db-downtime', toolName: 'db.equipment_downtime_report', agentName: 'Reliability', timestamp: '2026-04-18T08:45:00Z', durationMs: 1850, success: true },
  { id: 'rc-10', toolId: 'tool-mcp-iq-asset-getById', toolName: 'iq.asset.getById', agentName: 'Maintenance Intelligence', timestamp: '2026-04-18T08:40:00Z', durationMs: 18, success: true },
  { id: 'rc-11', toolId: 'tool-custom-notify', toolName: 'custom.send_shift_notification', agentName: 'Shift Handover', timestamp: '2026-04-18T06:00:00Z', durationMs: 320, success: true },
  { id: 'rc-12', toolId: 'tool-mcp-forge-inventory-reserveStock', toolName: 'forge.inventory.reserveStock', agentName: 'Parts & Inventory', timestamp: '2026-04-18T05:30:00Z', durationMs: 780, success: false, error: 'Timeout: upstream server did not respond within 30s' },
];

// ══════════════════════════════════════════════════════════════════════════
// PROMPTS
// ══════════════════════════════════════════════════════════════════════════

export type PromptCategory = 'operations' | 'safety' | 'compliance' | 'maintenance' | 'reporting' | 'general' | 'custom';
export type PromptScope = 'personal' | 'global';
export type PromptVarType = 'text' | 'select' | 'date' | 'number' | 'textarea' | 'boolean';

export interface PromptVariable {
  name: string;
  label: string;
  type: PromptVarType;
  required: boolean;
  defaultValue?: string;
  options?: string[];
  placeholder?: string;
}

export interface StudioPrompt {
  id: string;
  command: string;
  title: string;
  description: string;
  content: string;
  variables: PromptVariable[];
  category: PromptCategory;
  scope: PromptScope;
  usageCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const PROMPT_CATEGORY_LABELS: Record<PromptCategory, { label: string; color: string }> = {
  operations: { label: 'Operations', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  safety: { label: 'Safety', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  compliance: { label: 'Compliance', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  maintenance: { label: 'Maintenance', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' },
  reporting: { label: 'Reporting', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  general: { label: 'General', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
  custom: { label: 'Custom', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
};

export const MOCK_STUDIO_PROMPTS: StudioPrompt[] = [
  {
    id: 'prompt-shift-report',
    command: 'shift-report',
    title: 'Shift Report Template',
    description: 'Generate a structured shift handover report with alarms, work completed, and safety observations.',
    content: `Generate a shift handover report for {{facility}} for the {{shift}} shift on {{date}}.

Include:
1. Active alarms and their current status
2. Work orders completed during this shift
3. Open work orders and their priority
4. Equipment status changes (online/offline)
5. Safety incidents or near-misses
6. Items requiring attention from next shift

Format as a structured report with clear sections and bullet points.`,
    variables: [
      { name: 'facility', label: 'Facility', type: 'select', required: true, options: ['Boiler Room A', 'Boiler Room B', 'Control Room', 'Turbine Hall', 'Water Treatment'] },
      { name: 'shift', label: 'Shift', type: 'select', required: true, options: ['Day (06:00-18:00)', 'Night (18:00-06:00)'] },
      { name: 'date', label: 'Date', type: 'date', required: false, defaultValue: 'today', placeholder: 'YYYY-MM-DD' },
    ],
    category: 'reporting',
    scope: 'global',
    usageCount: 420,
    createdBy: 'Admin',
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-04-10T00:00:00Z',
  },
  {
    id: 'prompt-summarize',
    command: 'summarize',
    title: 'Document Summary',
    description: 'Summarize a document into key points at the specified detail level.',
    content: `Summarize the document "{{document_name}}" at a {{detail_level}} level of detail.

Focus on:
- Key findings and conclusions
- Action items or recommendations
- Important dates, numbers, or specifications
- Any safety or compliance implications

{{#if include_citations}}Include specific citations with page numbers for each key point.{{/if}}`,
    variables: [
      { name: 'document_name', label: 'Document Name', type: 'text', required: true, placeholder: 'e.g., Pump P-401 Maintenance Manual' },
      { name: 'detail_level', label: 'Detail Level', type: 'select', required: true, options: ['Brief (3-5 bullets)', 'Standard (1 page)', 'Detailed (2-3 pages)'] },
      { name: 'include_citations', label: 'Include Citations', type: 'boolean', required: false, defaultValue: 'true' },
    ],
    category: 'general',
    scope: 'global',
    usageCount: 234,
    createdBy: 'Admin',
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-03-15T00:00:00Z',
  },
  {
    id: 'prompt-compare',
    command: 'compare',
    title: 'Document Comparison',
    description: 'Compare two document versions or two different documents side by side.',
    content: `Compare "{{doc_a}}" with "{{doc_b}}".

Focus on these areas: {{focus_areas}}

For each difference found:
1. State what changed (was → now)
2. Assess the significance (minor / moderate / critical)
3. Note any safety or compliance implications`,
    variables: [
      { name: 'doc_a', label: 'First Document', type: 'text', required: true, placeholder: 'e.g., SOP-401 Rev 2.0' },
      { name: 'doc_b', label: 'Second Document', type: 'text', required: true, placeholder: 'e.g., SOP-401 Rev 3.0' },
      { name: 'focus_areas', label: 'Focus Areas', type: 'textarea', required: false, placeholder: 'e.g., safety procedures, equipment specs, regulatory references' },
    ],
    category: 'general',
    scope: 'global',
    usageCount: 89,
    createdBy: 'Admin',
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-03-20T00:00:00Z',
  },
  {
    id: 'prompt-safety-review',
    command: 'safety-review',
    title: 'Safety Procedure Review',
    description: 'Review a procedure against safety standards and flag potential issues.',
    content: `Review the safety procedure "{{procedure_name}}" against {{framework}} requirements.

Check for:
1. Completeness of LOTO steps
2. PPE requirements listed and correct
3. ANSI Z535.6 signal words present (DANGER, WARNING, CAUTION)
4. Emergency contact information current
5. Training requirements specified
6. Revision date within 12 months

Provide a compliance score (1-10) and list any gaps found.`,
    variables: [
      { name: 'procedure_name', label: 'Procedure Name', type: 'text', required: true, placeholder: 'e.g., LOTO for Pump P-401' },
      { name: 'framework', label: 'Framework', type: 'select', required: true, options: ['OSHA 1910.147', 'ANSI Z535.6', 'NFPA 70E', 'Company HSE Standards'] },
    ],
    category: 'safety',
    scope: 'global',
    usageCount: 156,
    createdBy: 'Safety Team',
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-04-01T00:00:00Z',
  },
  {
    id: 'prompt-troubleshoot',
    command: 'troubleshoot',
    title: 'Equipment Troubleshooting',
    description: 'Step-by-step troubleshooting guide for equipment issues.',
    content: `I need help troubleshooting {{equipment}}.

Symptoms: {{symptoms}}

Please provide:
1. Most likely root causes (ranked by probability)
2. Step-by-step diagnostic procedure
3. Required tools and instruments
4. Safety precautions before starting
5. Expected resolution time
6. When to escalate to specialist

Base your analysis on the equipment's maintenance history and similar past incidents.`,
    variables: [
      { name: 'equipment', label: 'Equipment', type: 'text', required: true, placeholder: 'e.g., Compressor C-201' },
      { name: 'symptoms', label: 'Symptoms', type: 'textarea', required: true, placeholder: 'Describe what you are observing...' },
    ],
    category: 'maintenance',
    scope: 'global',
    usageCount: 178,
    createdBy: 'Admin',
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-04-05T00:00:00Z',
  },
  {
    id: 'prompt-compliance-check',
    command: 'compliance-check',
    title: 'Compliance Status Check',
    description: 'Check compliance status against a specific regulatory framework.',
    content: `Check our compliance status against {{framework}} requirements.

Scope: {{scope}}

Provide:
1. Overall compliance score (Red/Amber/Green)
2. List of compliant areas
3. List of gaps with severity
4. Upcoming certification deadlines
5. Recommended remediation actions with priority`,
    variables: [
      { name: 'framework', label: 'Framework', type: 'select', required: true, options: ['ITAR', 'CMMC Level 2', 'HIPAA', 'GDPR', 'ISO 27001', 'SOC 2'] },
      { name: 'scope', label: 'Scope', type: 'select', required: false, options: ['Full organization', 'IT systems only', 'Specific department', 'Physical security'], defaultValue: 'Full organization' },
    ],
    category: 'compliance',
    scope: 'global',
    usageCount: 92,
    createdBy: 'Compliance Team',
    createdAt: '2026-03-10T00:00:00Z',
    updatedAt: '2026-04-08T00:00:00Z',
  },
  {
    id: 'prompt-wo-plan',
    command: 'plan-wo',
    title: 'Work Order Planning',
    description: 'Generate a detailed work order plan with resources, parts, and scheduling.',
    content: `Plan a work order for {{work_type}} on {{equipment}}.

Priority: {{priority}}

Generate:
1. Scope of work description
2. Required labor (craft, hours, crew size)
3. Parts and materials list with stock check
4. Required permits (LOTO, hot work, confined space)
5. Estimated duration
6. Suggested scheduling window
7. Pre-job safety briefing checklist`,
    variables: [
      { name: 'equipment', label: 'Equipment', type: 'text', required: true, placeholder: 'e.g., Heat Exchanger HX-301' },
      { name: 'work_type', label: 'Work Type', type: 'select', required: true, options: ['Corrective Maintenance', 'Preventive Maintenance', 'Inspection', 'Overhaul', 'Emergency Repair'] },
      { name: 'priority', label: 'Priority', type: 'select', required: true, options: ['Low', 'Medium', 'High', 'Critical'] },
    ],
    category: 'operations',
    scope: 'global',
    usageCount: 145,
    createdBy: 'Admin',
    createdAt: '2026-03-15T00:00:00Z',
    updatedAt: '2026-04-12T00:00:00Z',
  },
  {
    id: 'prompt-my-daily',
    command: 'daily-brief',
    title: 'My Daily Briefing',
    description: 'Personal daily briefing with open tasks, upcoming PMs, and relevant alerts.',
    content: `Generate my daily briefing for {{date}}.

Include:
1. My assigned open work orders and their status
2. Upcoming preventive maintenance tasks this week
3. Active alarms in my areas of responsibility
4. Any overdue items requiring attention
5. Weather forecast if outdoor work is planned
6. Safety reminders for today's activities`,
    variables: [
      { name: 'date', label: 'Date', type: 'date', required: false, defaultValue: 'today' },
    ],
    category: 'operations',
    scope: 'personal',
    usageCount: 312,
    createdBy: 'You',
    createdAt: '2026-04-01T00:00:00Z',
    updatedAt: '2026-04-17T08:00:00Z',
  },
];
